import { IModifier } from '@Core/Events/EventModifiers/Modifier';
import { Animation, TrackEntry } from '@esotericsoftware/spine-pixi-v8';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import EventsCenter from '@Managers/Events/EventsCenter';
import { ALL_EVENTS_LIST, EGameEvents, EVENTS_NUMBER_PLACEHOLDER, isWildcardEvent } from '@Managers/Events/GameEvents';
import { GameScene } from '..';
import { filterEventsByModifiers, getAllEventModifiers, getEventWithoutParams } from '../Utils/ModifierUtils';
import SpineWrapper from './SpineWrapper';

export enum EAnimationModifiers {
    LANDSCAPE = 'LANDSCAPE',
    PORTRAIT = 'PORTRAIT',
    LOOP = 'LOOP',
    RESTART = 'RESTART',
    NO_RESTART = 'NO_RESTART',
    FREE_GAMES = 'FREE_GAMES',
    OF_A_KIND = 'OF_A_KIND',
    LOCK_AND_ROLL = 'LOCK_AND_ROLL',
    WALL_MULTIPLIER = 'WALL_MULTIPLIER',
    BASE_GAME = 'BASE_GAME',
    SPECIAL = 'SPECIAL',
    SYMBOL_COUNT_MODIFIER = 'SYMBOL_$_COUNT_$'
}
export default class SpineWrapperEventManager {
    public static readonly PARAMETER_CHARACTER: string = '#';
    public static readonly MODIFIER_CHARACTER: string = '/';

    public scene: GameScene;
    public spineWrapper: SpineWrapper;
    public currentlyPlayingGameEvent: string;
    public currentlyPlayingGameEventIds: number[] | undefined;
    public manualLoop: boolean = false;
    public force: boolean = false;
    protected readonly blockers: Map<string, () => boolean> = new Map();
    public localEventModifiers: IModifier[] = [];

    constructor(scene: GameScene, spineWrapper: SpineWrapper, subscribeForEvents: boolean, localEventModifiers: IModifier[]) {
        this.scene = scene;
        this.spineWrapper = spineWrapper;
        this.localEventModifiers = localEventModifiers;
        if (subscribeForEvents) this.addEventAnimations();
        this.scene.game.gameStore.on(EGameStoreKeys.CurrentOrientation, this.switchPlayingAnimationOnOrientationChange, this);
    }

    private addEventAnimations(): void {
        const allEvents: string[] = Object.keys(ALL_EVENTS_LIST);
        allEvents.forEach((gameEvent: string) => {
            if (this.hasGameEventAnimation(gameEvent, true)) {
                EventsCenter.on(gameEvent, (data?: { eventIds: number[] }) => {
                    if (this.spineWrapper.spine.visible) this.playEventAnimation(gameEvent, data?.eventIds);
                });
            }
        });
    }

    public switchPlayingAnimationOnOrientationChange(): void {
        const currentTrack = this.spineWrapper.getCurrentAnimationTrack();
        if (currentTrack && this.hasGameEventAnimation(this.currentlyPlayingGameEvent) && this.spineWrapper.spine.visible) {
            const previousState = {
                animationName: currentTrack.animation?.name,
                trackTime: currentTrack.trackTime,
                loop: currentTrack.loop || this.manualLoop,
                force: this.force
            };
            this.playEventAnimation(this.currentlyPlayingGameEvent, this.currentlyPlayingGameEventIds, undefined, true, previousState);
        }
    }

    public getGameEventFromWildcardEvent(gameEvent: string, ids?: number[]): string {
        if (!isWildcardEvent(gameEvent)) {
            return gameEvent;
        }

        let finalIds = ids;

        if (!finalIds) {
            const animationName = getEventWithoutParams(this.spineWrapper.getCurrentAnimationName() ?? '');

            let matches = animationName.match(/\d+/g);

            if (!this.spineWrapper.getCurrentAnimationName()) {
                const animation = this.spineWrapper.spine.skeleton.data.animations.find((animation) => {
                    return getEventWithoutParams(animation.name).includes(gameEvent.replaceAll(EVENTS_NUMBER_PLACEHOLDER, ''));
                });

                matches = getEventWithoutParams(animation?.name ?? '').match(/\d+/g);
            }

            finalIds = matches?.map(Number) ?? [1];
        }

        let idIndex = 0;

        return gameEvent.replaceAll(EVENTS_NUMBER_PLACEHOLDER, () => {
            return (finalIds[idIndex++] ?? 1).toString();
        });
    }

    public getAnimationNameForGameEvent(gameEvent: string, ids?: number[]): string | undefined {
        const animations = this.spineWrapper.spine.skeleton.data.animations;
        const actualGameEvent = this.getGameEventFromWildcardEvent(gameEvent, ids);
        const animationsForEvent = animations
            .filter((animation: Animation) => getEventWithoutParams(animation.name) === actualGameEvent)
            .map((animation) => animation.name);
        if (animationsForEvent.length === 0) return undefined;
        const modifiers = [...this.scene.game.globalEventModifiers, ...this.localEventModifiers];
        const finalAnimationNames = filterEventsByModifiers(animationsForEvent, modifiers);
        return finalAnimationNames.length > 0 ? finalAnimationNames[0] : undefined;
    }

    public hasGameEventAnimation(gameEvent: string, skipModifiers?: boolean, ids?: number[]): boolean {
        if (skipModifiers) {
            const animations = this.spineWrapper.spine.skeleton.data.animations;
            const actualGameEvent = this.getGameEventFromWildcardEvent(gameEvent);
            const animationsForEvent = animations.filter((animation: Animation) => getEventWithoutParams(animation.name) === actualGameEvent);
            return animationsForEvent.length > 0;
        }

        return !!this.getAnimationNameForGameEvent(gameEvent, ids);
    }

    public playEventAnimation(
        eventName: string,
        ids?: number[],
        trackIndex: number = 0,
        orientationChanged: boolean = false,
        previousState?: { animationName?: string; trackTime?: number; loop?: boolean; force: boolean },
        loop: boolean = false,
        force: boolean = false
    ): TrackEntry | void {
        if (!this.spineWrapper.spine.visible) return;
        const animationName = this.getAnimationNameForGameEvent(eventName, ids);
        if (!animationName) return;
        const animationParams = getAllEventModifiers(animationName);
        const isLooped = animationParams?.includes(EAnimationModifiers.LOOP) || loop || !!previousState?.loop;
        const hasRestartFlag = animationParams?.includes(EAnimationModifiers.RESTART);
        const hasNoRestartFlag = animationParams?.includes(EAnimationModifiers.NO_RESTART);
        const isPlaying = animationName ? this.isAnimationPlaying(animationName, trackIndex, isLooped) : false;
        const isPlayingNonRestartable = isPlaying && ((isLooped && !hasRestartFlag) || (!isLooped && hasNoRestartFlag));
        const isLastAnimation = eventName === this.currentlyPlayingGameEvent;
        const shouldIgnoreBlockers = orientationChanged && isLastAnimation;

        if (this.isBlocked() && !shouldIgnoreBlockers) return; // TODO: użyć tego że w reel symbolu blocker ustawić

        if (!animationName) return console.warn('Trying to play non existing animation for event: ' + eventName, this);

        this.warnWrongModifiers(animationName, isLooped, hasRestartFlag, hasNoRestartFlag);

        if (!orientationChanged && isPlayingNonRestartable && !force && !previousState?.force) return;

        this.currentlyPlayingGameEvent = eventName;
        this.currentlyPlayingGameEventIds = ids;
        this.manualLoop = loop;
        this.force = force;

        const currentTrack = this.spineWrapper.spine.state.getTrack(trackIndex);

        if (currentTrack && !currentTrack.isComplete() && currentTrack.animation?.name !== animationName) {
            this.applyLastFrameOfCurrentAnimation(trackIndex);
        }

        const trackEntry = this.spineWrapper.play(animationName, isLooped, trackIndex, shouldIgnoreBlockers);

        this.restorePreviousTrackTime(animationName, orientationChanged, previousState);

        return trackEntry;
    }

    public isCurrentlyPlayingGameEvent(gameEvent: string, ids?: number[]): boolean {
        if (this.currentlyPlayingGameEvent !== gameEvent) return false;
        return SpineWrapperEventManager.idsMatch(this.currentlyPlayingGameEventIds, ids);
    }

    private static idsMatch(a?: number[], b?: number[]): boolean {
        const arrA = a ?? [];
        const arrB = b ?? [];
        if (arrA.length !== arrB.length) return false;
        return arrA.every((value, index) => value === arrB[index]);
    }

    public isBlocked(): boolean {
        for (const blocker of this.blockers.values()) {
            if (blocker()) return true;
        }
        return false;
    }

    private warnWrongModifiers(animationName: string, isLooped: boolean, hasRestart: boolean, hasNoRestart: boolean) {
        if (isLooped && hasNoRestart) {
            console.warn(
                `[SpineWrapper] Looping animation "${animationName}" should not have ${EAnimationModifiers.NO_RESTART} flag. ` +
                    `By default, looped animations do not restart, so only ${EAnimationModifiers.RESTART} flag is allowed.`
            );
        }
        if (!isLooped && hasRestart) {
            console.warn(
                `[SpineWrapper] Non-looping animation "${animationName}" should not have ${EAnimationModifiers.RESTART} flag. ` +
                    `By default, non-looping animations restart automatically, so only ${EAnimationModifiers.NO_RESTART} flag is allowed.`
            );
        }
    }

    private restorePreviousTrackTime(animationName: string, orientationChanged: boolean, previousState?: { animationName?: string; trackTime?: number }) {
        if (!orientationChanged || !previousState?.animationName || previousState.trackTime === undefined) return;

        const prevAnimName = getEventWithoutParams(previousState.animationName);
        const newAnimName = getEventWithoutParams(animationName);

        if (prevAnimName === newAnimName) {
            this.spineWrapper.spine.state.update(previousState.trackTime);
        }
    }

    private isAnimationPlaying(animationName: string, trackIndex = 0, isLooped = false): boolean {
        const currentTrack = this.spineWrapper.spine.state.getTrack(trackIndex);
        const isSameAnimation = currentTrack?.animation?.name === animationName;
        const isActive = currentTrack && !currentTrack.isComplete();
        return isSameAnimation && (isActive || isLooped);
    }

    private getFullEventList(): string[] {
        const baseEvents: string[] = Object.keys(ALL_EVENTS_LIST);
        const fullEvents = [...baseEvents];
        baseEvents.forEach((event) => {
            if (event.includes(EVENTS_NUMBER_PLACEHOLDER)) {
                this.addDynamicEvents(fullEvents, event);
            }
        });
        return fullEvents;
    }

    private addDynamicEvents(allEvents: string[], placeholderEvent: string): void {
        const eventNamePrefix = placeholderEvent.replace(EVENTS_NUMBER_PLACEHOLDER, '');
        const spineData = this.spineWrapper.spine.skeleton.data;
        const numberSuffixRegex = /\d+\b/;
        const matchingAnimations = spineData.animations
            .map((animation) => getEventWithoutParams(animation.name))
            .filter((animName) => animName.startsWith(eventNamePrefix) && numberSuffixRegex.test(animName.slice(eventNamePrefix.length)));

        matchingAnimations.forEach((animName) => {
            allEvents.push(animName);
        });
    }

    public countWildcardAnimations(gameEvent: EGameEvents): number {
        const eventPrefix = gameEvent.slice(0, -1);
        return new Set(
            this.spineWrapper.spine.state.data.skeletonData.animations
                .map((animation) => getEventWithoutParams(animation.name))
                .filter((name) => name.startsWith(eventPrefix))
        ).size;
    }

    public registerBlocker(name: string, blocker: () => boolean): void {
        this.blockers.set(name, blocker);
    }

    public unregisterBlocker(name: string): void {
        this.blockers.delete(name);
    }

    public hasGameEventAnimations(events: string[], skipModifiers = false, eventIds: number[] = []): boolean {
        return events.every((event, index) => this.hasGameEventAnimation(event, skipModifiers, [eventIds[index]]));
    }

    public applyLastFrameOfCurrentAnimation(trackIndex = 0) {
        const state = this.spineWrapper.spine.state;
        const skeleton = this.spineWrapper.spine.skeleton;
        const current = state.getTrack(trackIndex);

        if (!current?.animation) return;
        current.trackTime = current.animation.duration;
        state.apply(skeleton);
    }
}
